var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { MeshPhysicalMaterial, MeshStandardMaterial, ShaderMaterial } from 'three';
import { assertUnreachable } from '@canvas-logic/clp-utils';
import { MaterialDefaultsCache } from './MaterialDefaultsCache';
import { convertValue, getSchemaFor, TEXTURE_PARAM_NAMES } from './MaterialSchema';
export class MaterialsManager {
    constructor(assetsManager) {
        this.assetsManager = assetsManager;
        this.materialsParams = new Map();
        this.materials = new Map();
    }
    clear() {
        this.materialsParams.clear();
    }
    addMaterial(options) {
        this.updateOrCreate(options.key, options.params);
    }
    has(key) {
        return this.materials.has(key);
    }
    get(key) {
        return this.materials.get(key);
    }
    getThreeJsParams(params) {
        const { type } = params, threeJsParams = __rest(params, ["type"]);
        return threeJsParams;
    }
    updateOrCreate(key, params) {
        const existing = this.materials.get(key);
        const prevParams = this.materialsParams.get(key);
        this.materialsParams.set(key, params);
        if (!existing) {
            const newMat = this.createMaterial(key, params);
            this.materials.set(key, newMat);
            return newMat;
        }
        this.updateMaterial(existing, prevParams, params);
        return existing;
    }
    updateMaterial(material, prevParams, newParams) {
        const params = this.getThreeJsParams(newParams);
        const oldParams = !prevParams ? {} : this.getThreeJsParams(prevParams);
        const schema = getSchemaFor(newParams.type);
        for (const key of Object.keys(params)) {
            const value = params[key];
            if (value === oldParams[key])
                continue;
            const paramSchema = schema[key];
            if (paramSchema) {
                material[key] = convertValue(paramSchema.kind, value, this.assetsManager);
                if (paramSchema.requiresNeedsUpdateOnChange)
                    material.needsUpdate = true;
            }
            else {
                material[key] = value;
                material.needsUpdate = true;
            }
        }
        for (const key of Object.keys(oldParams)) {
            if (!(key in params)) {
                this.resetMaterialProperty(material, key);
                const paramSchema = schema[key];
                if (!paramSchema || paramSchema.requiresNeedsUpdateOnChange) {
                    material.needsUpdate = true;
                }
            }
        }
    }
    createMaterial(key, params) {
        let material;
        const type = params.type;
        switch (type) {
            case 'shaderMaterial':
                material = this.createShaderMaterial(key, params);
                break;
            case 'standardMaterial':
                material = this.createStandardMaterial(key, params);
                break;
            case 'physicalMaterial':
                material = this.createPhysicalMaterial(key, params);
                break;
            default:
                assertUnreachable(type, `Unhandled material type ${type}`);
        }
        if (material === undefined) {
            throw new Error(`Cannot find material "${key}"`);
        }
        material.name = key;
        return material;
    }
    createShaderMaterial(materialName, params) {
        var _a, _b;
        const materialUniforms = Object.entries((_a = params.uniforms) !== null && _a !== void 0 ? _a : {});
        const uniforms = {};
        materialUniforms.map(([textureName, textureKey]) => {
            uniforms[textureName] = { value: this.assetsManager.getAsset(textureKey) };
        });
        const material = new ShaderMaterial({
            fragmentShader: params.fragmentShader,
            vertexShader: params.vertexShader,
            transparent: (_b = params.transparent) !== null && _b !== void 0 ? _b : false,
            uniforms,
        });
        material.needsUpdate = true;
        material.name = materialName;
        return material;
    }
    createStandardMaterial(materialName, params) {
        const maps = this.getMaterialTextures(params);
        const threeJsParams = this.getThreeJsParams(params);
        const materialParams = Object.assign(Object.assign({}, threeJsParams), maps);
        const material = new MeshStandardMaterial(Object.assign({}, materialParams));
        return material;
    }
    getMaterialTextures(params) {
        const result = {};
        for (const mapName of TEXTURE_PARAM_NAMES) {
            const textureKey = params[mapName];
            if (textureKey) {
                const texture = this.assetsManager.getAsset(textureKey);
                if (!texture) {
                    throw new Error(`Cannot load texture for ${mapName} with key ${params[mapName]}`);
                }
                result[mapName] = texture;
            }
        }
        return result;
    }
    createPhysicalMaterial(materialName, params) {
        const maps = this.getMaterialTextures(params);
        const threeJsParams = this.getThreeJsParams(params);
        const materialParams = Object.assign(Object.assign({}, threeJsParams), maps);
        const material = new MeshPhysicalMaterial(Object.assign({}, materialParams));
        material.name = materialName;
        return material;
    }
    resetMaterialProperty(material, key) {
        const defaultMaterial = MaterialDefaultsCache.getDefaultsFor(material);
        if (key in defaultMaterial) {
            material[key] = defaultMaterial[key];
        }
        else {
            console.warn(`You are trying to reset unknown material property ${key} in material type ${material.type}. Setting it to undefined`);
            material[key] = undefined;
        }
    }
}
//# sourceMappingURL=MaterialsManager.js.map